import { recommendedServiceEnum, visitRecommendations } from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import {
  ILP_HUOLTO_TASK_ID,
  SADEVESIKOURUT_TASK_ID,
  VENTILATION_WORK_TASK_IDS,
} from './servicePreset'

export const RECOMMENDED_SERVICES = recommendedServiceEnum.enumValues
export type RecommendedService = (typeof RECOMMENDED_SERVICES)[number]

// Finnish display names shared by the ERP and the customer email.
export const RECOMMENDED_SERVICE_LABELS: Record<RecommendedService, string> = {
  nuohous: 'Nuohous',
  ilp_syvapuhdistus: 'Ilmalämpöpumpun syväpuhdistus',
  iv_puhdistus: 'Ilmanvaihdon puhdistus',
  rannien_puhdistus: 'Räystäskourujen puhdistus',
  katon_pesu_pinnoitus: 'Katon pesu tai pinnoitus',
  ei_mikaan: 'Ei mikään',
}

/**
 * Which recommendable service a visit itself was, judged from its task rows.
 * Null when it is none of them (a survey, roof work without a fixed task id).
 * Keeps the current service out of the prompt and words the customer email.
 */
export type RecommendableService = Exclude<RecommendedService, 'ei_mikaan'>

export function detectVisitService(
  tasks: { maintenanceTaskId: number | null; taskName: string }[],
): RecommendableService | null {
  if (tasks.some((t) => /nuohous/i.test(t.taskName))) return 'nuohous'
  const ids = new Set(tasks.map((t) => t.maintenanceTaskId))
  if (ids.has(ILP_HUOLTO_TASK_ID)) return 'ilp_syvapuhdistus'
  if (VENTILATION_WORK_TASK_IDS.some((id) => ids.has(id))) return 'iv_puhdistus'
  if (ids.has(SADEVESIKOURUT_TASK_ID)) return 'rannien_puhdistus'
  return null
}

export const selectVisitRecommendationSchema = createSelectSchema(
  visitRecommendations,
).omit({ createdAt: true, updatedAt: true, deletedAt: true })

export const insertVisitRecommendationSchema = createInsertSchema(
  visitRecommendations,
).pick({ visitId: true, service: true })

export const visitRecommendationStatsInputSchema = z.object({
  days: z.number().int().positive().default(30),
})

export const visitRecommendationStatsSchema = z.object({
  total: z.number(),
  none: z.number(),
  byService: z.array(
    z.object({
      service: z.enum(recommendedServiceEnum.enumValues),
      count: z.number(),
      emailsSent: z.number(),
      emailsOpened: z.number(),
      redeemed: z.number(),
      // Gross (VAT-inclusive) task revenue of the visits booked with the voucher.
      revenue: z.number(),
    }),
  ),
  byTechnician: z.array(
    z.object({
      userId: z.number(),
      name: z.string(),
      total: z.number(),
      none: z.number(),
    }),
  ),
})

export type VisitRecommendation = z.infer<
  typeof selectVisitRecommendationSchema
>
export type CreateVisitRecommendation = z.infer<
  typeof insertVisitRecommendationSchema
>
export type VisitRecommendationStats = z.infer<
  typeof visitRecommendationStatsSchema
>
